import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { RemovaCharEspeciais } from '@/utils/format'
import { useUtils } from '@/lib/hooks/utils'
import { useUpdateUserCep } from '@/features/users/services'
import { useNavigate } from 'react-router-dom'
import { FeedbackDialog } from '../components/form/feedback-dialog'
import { CEPInput } from '../components/form/input/cep-mask-input'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { ROUTES } from '@/config/routes'

const schema = z.object({
  cep: z
    .string()
    .min(8, 'CEP é obrigatório')
    .regex(/^\d{5}-?\d{3}$/, 'CEP inválido'),
  bairro: z.string().min(1, 'Bairro é obrigatório'),
  numero: z.string().min(1, 'Número é obrigatório'),
  localidade: z.string().min(1, 'Município é obrigatório'),
  uf: z.string().min(1, 'UF é obrigatório'),
  logradouro: z.string().min(1, 'Logradouro é obrigatório'),
  isInfoCorrect: z.literal(true, {
    errorMap: () => ({ message: 'Você deve confirmar que as informações estão corretas' })
  })
})

type FormValues = z.infer<typeof schema>

export default function UpdateCepUsuario() {
  const { buscarCEP } = useUtils()
  const { mutate: updateCep } = useUpdateUserCep()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isCepLoading, setIsCepLoading] = useState(false)
  const navigate = useNavigate()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      cep: '',
      bairro: '',
      numero: '',
      localidade: '',
      uf: '',
      logradouro: ''
    },
    mode: 'onChange',
    shouldFocusError: true
  })

  const handleCepChange = async (value: string) => {
    form.setValue('cep', value)
    const cleanCep = RemovaCharEspeciais(value)
    if (cleanCep.length === 8) {
      setIsCepLoading(true)
      try {
        const response = await buscarCEP(cleanCep)
        if (!response) throw new Error('Nenhuma informação encontrada para este CEP.')
        form.setValue('bairro', response.bairro || '')
        form.setValue('numero', response.numero || '')
        form.setValue('localidade', response.localidade || '')
        form.setValue('uf', response.uf || '')
        form.setValue('logradouro', response.logradouro || '')
        form.trigger(['bairro', 'numero', 'localidade', 'uf', 'logradouro'])
        setMessage(null)
      } catch (err) {
        setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao buscar CEP.' })
        form.resetField('bairro')
        form.resetField('numero')
        form.resetField('localidade')
        form.resetField('uf')
        form.resetField('logradouro')
      } finally {
        setIsCepLoading(false)
      }
    }
  }

  const onSubmit = async (data: FormValues) => {
    const body = {
      cep: RemovaCharEspeciais(data.cep),
      logradouro: data.logradouro,
      bairro: data.bairro,
      numero: data.numero,
      municipio: data.localidade,
      estado: data.uf
    }

    try {
      updateCep(
        { body },
        {
          onSuccess: () => {
            setMessage({ type: 'success', text: 'O CEP foi atualizado com sucesso!' })
            setIsDialogOpen(true)
            navigate(ROUTES.MINUTE_USER.path, { replace: true })
          },
          onError: () => {
            setMessage({ type: 'error', text: 'Houve um erro ao atualizar o CEP.' })
            setIsDialogOpen(true)
          }
        }
      )
    } catch {
      setMessage({ type: 'error', text: 'Erro ao criar usuário.' })
    }
  }

  const handleSendDraft = form.handleSubmit(async data => {
    await onSubmit(data)
  })

  return (
    <div className="min-h-screen flex justify-center items-center">
      <Card className="bg-background w-full max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="text-foreground">Cadastrar Endereço</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="cep"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <CEPInput
                          value={field.value}
                          onChange={async v => {
                            field.onChange(v)
                            await handleCepChange(v)
                          }}
                          loading={isCepLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bairro"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="numero"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="localidade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Município</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="uf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>UF</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="logradouro"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logradouro</FormLabel>
                      <FormControl>
                        <Input {...field} className="bg-input" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="isInfoCorrect"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 mt-4">
                    <FormControl>
                      <Checkbox
                        id="terms"
                        checked={field.value}
                        onCheckedChange={checked => field.onChange(!!checked)}
                        className="bg-input"
                      />
                    </FormControl>
                    <FormLabel htmlFor="terms" className="text-sm font-medium text-foreground">
                      Todas as informações preenchidas acima estão corretas
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-between mt-6">
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary-hover text-primary-foreground"
                  onClick={handleSendDraft}
                  disabled={!form.watch('isInfoCorrect')}
                >
                  Continuar
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
        <CardFooter />
      </Card>
      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </div>
  )
}
